/**
 * Numeric PIN entry — shared by the lock screen and the setup flow.
 *
 * Renders its own keypad rather than a TextInput with a numeric keyboard: the
 * system keyboard offers autocorrect, clipboard, and (on Android) third-party
 * IMEs, none of which should ever see a PIN.
 */
import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";

import { colors, fonts } from "@/lib/theme";

const ROWS = [
    ["1", "2", "3"],
    ["4", "5", "6"],
    ["7", "8", "9"],
    ["", "0", "del"]
] as const;

interface PinPadProps {
    title: string;
    subtitle?: string;
    value: string;
    length: number;
    error?: string | null;
    disabled?: boolean;
    onChange: (next: string) => void;
    footer?: React.ReactNode;
    /**
     * Position in a multi-step flow, e.g. { current: 2, total: 2 }.
     *
     * Setting a PIN asks for the same six digits twice, and the second ask is
     * easy to miss — the screen looks near-identical to the first. Naming the
     * step makes the repetition legible instead of surprising.
     */
    step?: { current: number; total: number };
    /** Emphasised instruction above the dots. Used to call out the re-entry. */
    notice?: string;
}

export function PinPad({
    title,
    subtitle,
    value,
    length,
    error,
    disabled = false,
    onChange,
    footer,
    step,
    notice
}: PinPadProps) {
    const press = (key: string) => {
        if (disabled) {
            return;
        }
        if (key === "del") {
            onChange(value.slice(0, -1));
            return;
        }
        if (!key || value.length >= length) {
            return;
        }
        onChange(value + key);
    };

    return (
        <View style={styles.container}>
            <View style={styles.header}>
                {step ? (
                    <View style={styles.stepRow}>
                        {Array.from({ length: step.total }).map((_, i) => (
                            <View
                                key={i}
                                style={[styles.stepBar, i < step.current && styles.stepBarDone]}
                            />
                        ))}
                        <Text style={styles.stepText}>
                            Step {step.current} of {step.total}
                        </Text>
                    </View>
                ) : null}
                <Text style={styles.title}>{title}</Text>
                {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
            </View>

            {notice ? (
                <View style={styles.notice}>
                    <Text style={styles.noticeText}>{notice}</Text>
                </View>
            ) : null}

            <View style={styles.dots}>
                {Array.from({ length }).map((_, i) => (
                    <View
                        key={i}
                        style={[
                            styles.dot,
                            i < value.length && styles.dotFilled,
                            error ? styles.dotError : null
                        ]}
                    />
                ))}
            </View>

            <Text style={styles.error} numberOfLines={2}>
                {error ?? " "}
            </Text>

            <View style={styles.keypad}>
                {ROWS.map((row, ri) => (
                    <View key={ri} style={styles.row}>
                        {row.map((key, ki) =>
                            key === "" ? (
                                <View key={ki} style={styles.key} />
                            ) : (
                                <TouchableOpacity
                                    key={ki}
                                    style={[styles.key, disabled && styles.keyDisabled]}
                                    onPress={() => press(key)}
                                    activeOpacity={0.6}
                                    accessibilityRole="button"
                                    accessibilityLabel={key === "del" ? "Delete" : key}
                                >
                                    <Text style={styles.keyText}>{key === "del" ? "⌫" : key}</Text>
                                </TouchableOpacity>
                            )
                        )}
                    </View>
                ))}
            </View>

            {footer ? <View style={styles.footer}>{footer}</View> : null}
        </View>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, alignItems: "center", justifyContent: "center", paddingHorizontal: 24 },
    header: { alignItems: "center", marginBottom: 20 },
    stepRow: { flexDirection: "row", alignItems: "center", gap: 6, marginBottom: 16 },
    stepBar: {
        width: 26,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.gray300
    },
    stepBarDone: { backgroundColor: colors.teal400 },
    stepText: {
        marginLeft: 6,
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.gray500,
        letterSpacing: 0.6,
        textTransform: "uppercase"
    },
    notice: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.teal50,
        borderColor: colors.teal200,
        borderWidth: 1,
        borderRadius: 12,
        paddingVertical: 12,
        paddingHorizontal: 16,
        marginBottom: 24,
        maxWidth: 330
    },
    noticeText: {
        fontSize: 14,
        fontFamily: fonts.semiBold,
        color: colors.teal700,
        textAlign: "center",
        lineHeight: 20
    },
    title: {
        fontSize: 24,
        fontFamily: fonts.bold,
        color: colors.gray900,
        textAlign: "center"
    },
    subtitle: {
        fontSize: 15,
        fontFamily: fonts.regular,
        color: colors.gray600,
        textAlign: "center",
        marginTop: 8,
        lineHeight: 21
    },
    dots: { flexDirection: "row", gap: 16 },
    dot: {
        width: 16,
        height: 16,
        borderRadius: 8,
        borderWidth: 2,
        borderColor: colors.gray300
    },
    dotFilled: { backgroundColor: colors.teal400, borderColor: colors.teal400 },
    dotError: { borderColor: colors.error500 },
    error: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.error600,
        textAlign: "center",
        marginTop: 16,
        marginBottom: 8,
        minHeight: 20
    },
    keypad: { width: "100%", maxWidth: 300 },
    row: { flexDirection: "row", justifyContent: "space-between", marginBottom: 12 },
    key: {
        width: 76,
        height: 76,
        borderRadius: 38,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.gray100
    },
    keyDisabled: { opacity: 0.4 },
    keyText: { fontSize: 26, fontFamily: fonts.medium, color: colors.gray900 },
    footer: { marginTop: 20, alignItems: "center" }
});
